import { useEffect } from 'preact/hooks';
import { useRouterState } from '@tanstack/react-router';
import { trackPageView } from '../instrumentation.ts';
import { useRouteFullPath } from './useRoute.ts';

// Records a `gp.ui.pageview` dwell span for the current route, re-emitting once per
// visit. See `trackPageView` and docs/ui-instrumentation.md for the metric.
//
// The `page` attribute is the matched route's `fullPath` (e.g. `/features/documents`)
// so it stays low-cardinality and lines up with the routes Sentry's TanStack
// integration emits. But the effect keys on the resolved `pathname`, not
// `page`: on a param change the page component usually stays mounted and
// `fullPath` is unchanged, so keying on `page` would merge the two visits into
// a single span. Keying on `pathname` re-fires the effect — the old span emits
// as `spa-navigation`, a fresh clock starts — for each distinct URL.
//
// Called once from the root layout (`router.tsx`) so every route emits a visit.
// It reads the leaf match's `fullPath`, so one call site still names each page.
// A second call in a page component would double-count the visit.
export function usePageView(): void {
  const page = useRouteFullPath();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => trackPageView(page), [pathname, page]);
}
